import { LoadingOutlined } from '@ant-design/icons';
import {
  Col,
  Form,
  Input,
  notification,
  Row,
  Select,
  Switch,
  Typography,
} from 'antd';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';

import { SubTitle } from '../../../../components/Typography';
import {
  carregaCidades,
  carregaCidadesPorId,
} from '../../../../helpers/cidades';
import { formatarCnpjCpfIMask } from '../../../../helpers/formatador';
import apiNetCore from '../../../../services/apiNetCore';

export default function FormClienteOrigemDestino({
  tipoCliente,
  clienteCopy,
  title,
  required,
  form,
  dadosLote,
  disableField,
  enderecoDisabled,
  enderecoCompletoRequired,
}) {
  const {
    setFieldsValue,
    validateFields,
    getFieldsValue,
    getFieldValue,
  } = form;
  const [apiClientes, setApiClientes] = useState([]);
  const [apiCidades, setApiCidades] = useState([]);
  const [loadingClientes, setLoadingClientes] = useState(false);
  const [loadingCidades, setLoadingCidades] = useState(false);
  const [selectedEndereco, setSelectedEndereco] = useState(null);
  const [switchCliente, setSwitchCliente] = useState(false);

  const clearCidade = () => {
    setFieldsValue({
      [`cidade${tipoCliente}`]: undefined,
      [`ibgeCidade${tipoCliente}`]: undefined,
    });
  };

  const getLegendaSwtichCliente = cliente => {
    const nome = clienteCopy === 'Origem' ? 'Remetente' : 'Destinatário';
    return `${cliente} igual ao ${nome}`;
  };

  const getIdCliente = cliente => getFieldValue(`idCliente${cliente}`);

  const handleSearchClientes = async value => {
    if (!value || value.length < 3) return;

    value = value.replace(/[&\/\\#,+()$~%.'":*?<>{}-]/g, '');

    if (value.length < 3) return;

    setLoadingClientes(true);
    try {
      const { data } = await apiNetCore.get('/ClienteAts/GetClientes', {
        params: {
          clienteParametro: value,
        },
      });

      setApiClientes(data);
    } catch (err) {
      notification.error({
        message: 'Ops, não foi possível recuperar os clientes',
        description:
          'Por favor, entre em contato com nosso suporte caso o problema persistir.',
      });
    }
    setLoadingClientes(false);
  };

  const onSearchClientes = useDebouncedCallback(async value => {
    handleSearchClientes(value);
  }, 1000);

  const onSearchCidade = useDebouncedCallback(async value => {
    carregaCidades(value, setApiCidades, setLoadingCidades);
  }, 1000);

  const handleSelectCliente = (_value, { idcliente, idendereco }) => {
    if (!idcliente) return;

    const { clienteEndereco, idCliente, nomeFantasia } = apiClientes.find(
      apiCliente => apiCliente.idCliente === idcliente
    );

    const enderecoSelected =
      clienteEndereco.length > 0
        ? clienteEndereco.find(
            endereco => endereco.idClienteEndereco === idendereco
          )
        : '';

    onSearchCidade(enderecoSelected.cidade);
    setSelectedEndereco(idendereco);
    setFieldsValue({
      [`cliente${tipoCliente}`]:
        nomeFantasia || enderecoSelected.idClienteEndereco || idCliente,
      [`bairro${tipoCliente}`]: enderecoSelected.bairro || undefined,
      [`cep${tipoCliente}`]: enderecoSelected.cep || undefined,
      [`cidade${tipoCliente}`]: enderecoSelected.idCidade || undefined,
      [`complemento${tipoCliente}`]: enderecoSelected.complemento || undefined,
      [`endereco${tipoCliente}`]: enderecoSelected.endereco || undefined,
      [`estado${tipoCliente}`]: enderecoSelected.idEstado || undefined,
      [`numero${tipoCliente}`]: enderecoSelected.numero || undefined,
      [`idCliente${tipoCliente}`]: idCliente,
      [`idEndereco${tipoCliente}`]:
        enderecoSelected.idClienteEndereco || undefined,
      [`ibgeCidade${tipoCliente}`]: enderecoSelected.ibgeCidade || undefined,
    });

    validateFields([`cliente${tipoCliente}`]);
  };

  const capitalize = text =>
    text.toLowerCase().replace(/(?:^|\s)\S/g, a => a.toUpperCase());

  const selectOptionCliente = (cliente, endereco = '') => (
    <Select.Option
      key={endereco.idClienteEndereco || cliente.idCliente}
      value={endereco.idClienteEndereco || cliente.idCliente}
      idcliente={cliente.idCliente}
      idendereco={endereco.idClienteEndereco}
    >
      {`${cliente.razaoSocial} - ${formatarCnpjCpfIMask(cliente.cnpj)}`}
      <br />
      {endereco &&
        `(${endereco.uf}) ${capitalize(endereco.cidade)} - ${
          endereco.endereco
        } - ${endereco.bairro}`}
    </Select.Option>
  );

  const clearCliente = () => {
    setSelectedEndereco(null);

    setFieldsValue({
      [`cliente${tipoCliente}`]: undefined,
      [`bairro${tipoCliente}`]: undefined,
      [`cep${tipoCliente}`]: undefined,
      [`cidade${tipoCliente}`]: undefined,
      [`complemento${tipoCliente}`]: undefined,
      [`endereco${tipoCliente}`]: undefined,
      [`estado${tipoCliente}`]: undefined,
      [`numero${tipoCliente}`]: undefined,
      [`idCliente${tipoCliente}`]: undefined,
      [`idEndereco${tipoCliente}`]: undefined,
      [`ibgeCidade${tipoCliente}`]: undefined,
    });
  };

  const formatarEndereco = endereco => {
    const enderecoFormatado = {};

    Object.keys(endereco).forEach(dado => {
      enderecoFormatado[`${dado + tipoCliente}`] = endereco[dado];
    });

    return enderecoFormatado;
  };

  useEffect(() => {
    async function onInit() {
      if (Object.values(dadosLote).length === 0) return;

      const endereco = dadosLote[`endereco${tipoCliente}`];

      if (endereco.cidade) {
        carregaCidadesPorId(endereco.cidade, setApiCidades, setLoadingCidades);
      }

      setFieldsValue(formatarEndereco(endereco));
      setSelectedEndereco(endereco.cliente && endereco.idEndereco);

      // se o cliente remetente/destinatario for igual expedidor/recebedor, toggle default true
      if (
        getIdCliente(clienteCopy) &&
        getIdCliente(clienteCopy) === getIdCliente(tipoCliente)
      ) {
        setSwitchCliente(true);
      }
    }
    onInit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dadosLote]);

  const handleField = useCallback(() => {
    // se apagar o cliente remetente/destinatario, apaga no expedidor/recebedor
    if (clienteCopy && switchCliente && !getIdCliente(clienteCopy)) {
      setSwitchCliente(false);
      clearCliente();
      // Se alterar remetente/destinatário, desmarca toggle no expedidor/recebedor
    } else if (getIdCliente(tipoCliente) !== getIdCliente(clienteCopy)) {
      setSwitchCliente(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getFieldValue(`idCliente${clienteCopy}`)]);

  useEffect(() => {
    handleField();
  }, [handleField]);

  const handleSelectCidade = (value, option) => {
    if (!value) return;

    setFieldsValue({
      [`ibgeCidade${tipoCliente}`]: option.key,
      [`estado${tipoCliente}`]: option.idestado,
    });
  };

  const copiaOrigemDestino = cliente => {
    const {
      [`cliente${cliente}`]: nomeFantasia,
      [`bairro${cliente}`]: bairro,
      [`cep${cliente}`]: cep,
      [`cidade${cliente}`]: cidade,
      [`complemento${cliente}`]: complemento,
      [`endereco${cliente}`]: endereco,
      [`estado${cliente}`]: estado,
      [`numero${cliente}`]: numero,
      [`idCliente${cliente}`]: idCliente,
      [`idEndereco${cliente}`]: idEndereco,
      [`ibgeCidade${cliente}`]: ibgeCidade,
    } = getFieldsValue(true);

    carregaCidadesPorId(cidade, setApiCidades, setLoadingCidades);
    setSelectedEndereco(idEndereco);

    setFieldsValue({
      [`cliente${tipoCliente}`]: nomeFantasia,
      [`bairro${tipoCliente}`]: bairro,
      [`cep${tipoCliente}`]: cep,
      [`cidade${tipoCliente}`]: cidade,
      [`complemento${tipoCliente}`]: complemento,
      [`endereco${tipoCliente}`]: endereco,
      [`estado${tipoCliente}`]: estado,
      [`numero${tipoCliente}`]: numero,
      [`idCliente${tipoCliente}`]: idCliente,
      [`idEndereco${tipoCliente}`]: idEndereco,
      [`ibgeCidade${tipoCliente}`]: ibgeCidade,
    });

    validateFields([`cliente${tipoCliente}`]);
  };

  const onChangeSwitchCliente = checked => {
    if (checked) copiaOrigemDestino(clienteCopy);
    else clearCliente();

    setSwitchCliente(checked);
  };

  return (
    <>
      <Typography.Title level={3}>{title}</Typography.Title>
      {clienteCopy && (
        <Row className="pb-10">
          <SubTitle>
            <Switch
              disabled={!getIdCliente(clienteCopy) || disableField}
              checked={switchCliente}
              size="small"
              onChange={onChangeSwitchCliente}
            />
            &nbsp;
            {getLegendaSwtichCliente(tipoCliente)}
          </SubTitle>
        </Row>
      )}
      <Row gutter={12}>
        <Col span={24}>
          <Form.Item name={`cliente${tipoCliente}`} label="Cliente">
            <Select
              allowClear
              showSearch
              showArrow={false}
              placeholder="Pesquise por um cliente"
              filterOption={false}
              showAction={['click']}
              onSelect={handleSelectCliente}
              notFoundContent={loadingClientes ? <LoadingOutlined /> : null}
              onSearch={onSearchClientes}
              onClear={clearCliente}
              loading={loadingClientes}
              disabled={disableField || switchCliente}
            >
              {apiClientes.map(dado => {
                if (dado.clienteEndereco.length > 0) {
                  return dado.clienteEndereco.map(endereco =>
                    selectOptionCliente(dado, endereco)
                  );
                }
                return selectOptionCliente(dado);
              })}
            </Select>
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={12}>
        <Col span={11}>
          <Form.Item
            name={`cep${tipoCliente}`}
            label="CEP"
            rules={[{ required: enderecoCompletoRequired }]}
          >
            <Input
              className="total-width"
              disabled={
                enderecoDisabled ||
                selectedEndereco ||
                disableField ||
                switchCliente
              }
            />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={12}>
        <Col span={11}>
          <Form.Item
            name={`endereco${tipoCliente}`}
            label="Endereço"
            rules={[{ required: enderecoCompletoRequired }]}
          >
            <Input
              className="total-width"
              disabled={
                enderecoDisabled ||
                selectedEndereco ||
                disableField ||
                switchCliente
              }
            />
          </Form.Item>
        </Col>
        <Col span={12} offset={1}>
          <Row gutter={12}>
            <Col span={11}>
              <Form.Item name={`numero${tipoCliente}`} label="Número">
                <Input
                  className="total-width"
                  disabled={
                    enderecoDisabled ||
                    selectedEndereco ||
                    disableField ||
                    switchCliente
                  }
                />
              </Form.Item>
            </Col>
            <Col offset={1} span={12}>
              <Form.Item name={`complemento${tipoCliente}`} label="Complemento">
                <Input
                  className="total-width"
                  disabled={
                    enderecoDisabled ||
                    selectedEndereco ||
                    disableField ||
                    switchCliente
                  }
                />
              </Form.Item>
            </Col>
          </Row>
        </Col>
      </Row>
      <Row gutter={12}>
        <Col span={11}>
          <Form.Item
            name={`cidade${tipoCliente}`}
            label="Cidade"
            rules={[{ required, message: 'Por favor, selecione uma cidade!' }]}
          >
            <Select
              showSearch
              showArrow={false}
              placeholder="Pesquise por uma cidade"
              autoComplete="new-password"
              showAction={['focus', 'click']}
              disabled={selectedEndereco || disableField || switchCliente}
              onChange={handleSelectCidade}
              onClear={clearCidade}
              onSearch={onSearchCidade}
              filterOption={false}
              allowClear={!required}
              loading={loadingCidades}
              notFoundContent={loadingCidades ? <LoadingOutlined /> : null}
            >
              {apiCidades.map(dado => (
                <Select.Option
                  key={dado.ibge}
                  idestado={dado.idEstado}
                  value={dado.idCidade}
                >
                  {`${dado.cidadeNome.toUpperCase()} - ${dado.sigla.toUpperCase()}`}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col span={12} offset={1}>
          <Form.Item
            name={`bairro${tipoCliente}`}
            label="Bairro"
            rules={[{ required: enderecoCompletoRequired }]}
          >
            <Input
              className="total-width"
              disabled={
                enderecoDisabled ||
                selectedEndereco ||
                disableField ||
                switchCliente
              }
            />
          </Form.Item>
        </Col>
      </Row>
    </>
  );
}

FormClienteOrigemDestino.propTypes = {
  tipoCliente: PropTypes.string.isRequired,
  clienteCopy: PropTypes.string,
  title: PropTypes.string.isRequired,
  required: PropTypes.bool,
  form: PropTypes.objectOf(
    PropTypes.oneOfType([PropTypes.func, PropTypes.object])
  ).isRequired,
  dadosLote: PropTypes.objectOf(PropTypes.any).isRequired,
  disableField: PropTypes.bool.isRequired,
  enderecoDisabled: PropTypes.bool.isRequired,
  enderecoCompletoRequired: PropTypes.bool.isRequired,
};

FormClienteOrigemDestino.defaultProps = {
  required: false,
  clienteCopy: '',
};
